import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import notify from "devextreme/ui/notify";
import { MESSAGES } from "../../common/Messages";
import "./dispatchManagement.css";
import { TAB_DEFAULT_HIDDEN_TOP_TABS, TAB_DEFAULT_TO_TOP_TAB, TOP_TABS } from "./constants";
import { getEffectiveDispatchingProfile } from "./services/DispatchManagementService";
import PickTab from "./pickTab/PickTab";
import DelTab from "./delTab/DelTab";
import CurrentTab from "./currentTab/CurrentTab";
import BottomTabs from "./bottom/BottomTabs";

/** Top tabs in the Delphi's own order, with the captions from uFMD0000.dfm minus the accelerators. */
const ALL_TOP_TABS = [
    { id: TOP_TABS.PICK, title: "Pick", Component: PickTab },
    { id: TOP_TABS.DEL, title: "Del", Component: DelTab },
    { id: TOP_TABS.CURRENT, title: "Current", Component: CurrentTab },
];

/**
 * The top tabs a profile leaves visible, from FMDISPATCHINGDP.TABDEFAULTS.
 *
 * SetDispatchingTabs (uFMD0000.pas:10648) shows every tab and then hides by value: 'P' hides Del,
 * 'D' hides Pick, 'C' hides both. 'A', an absent profile and any unrecognised value leave all three,
 * which is the routine's own default - it only ever removes.
 */
const visibleTopTabs = (profile) => {
    const hidden = TAB_DEFAULT_HIDDEN_TOP_TABS[profile?.TabDefaults] ?? [];
    return hidden.length === 0 ? ALL_TOP_TABS : ALL_TOP_TABS.filter((tab) => !hidden.includes(tab.id));
};

/*
 * Slightly under half to the top pane. The bottom pane carries a fourteen-tab strip above its grid,
 * so an even split leaves it the less usable of the two. The user's own drag is remembered and
 * overrides this.
 */
const DEFAULT_TOP_FRACTION = 0.45;

const SPLITTER_STORAGE_KEY = "fm.dispatchManagement.splitFraction";

/**
 * Keep in step with .dm-splitter's height in dispatchManagement.css.
 *
 * 8 is DevExtreme's own DEFAULT_RESIZE_HANDLE_SIZE (esm/__internal/ui/splitter/resize_handle.js),
 * the thickness the Splitter in AppContainer.js gives the sidebar's handle - see the note on the
 * splitter markup below for why this one is measured against that one.
 */
const SPLITTER_HEIGHT_PX = 8;

/*
 * The least a pane can be and still show a row.
 *
 * A grid here carries chrome that never shrinks, and these are measured against the rendered board
 * rather than estimated: header panel 46, column headers and filter row 68, filter panel 40 and pager
 * 54 - 208px - under a tab strip of another 38, so 246 before a single row is drawn. The rows area
 * gives up everything else (it is a scroll container, so flex lets it shrink to nothing), but those
 * 246 are fixed, and the panes clip rather than scroll - see .dm-pane .dx-multiview-item in the CSS.
 *
 * The floor was 240 against an estimate of 225, which put it *under* the real chrome: a pane sitting
 * on it drew its headers, filter panel and pager and nothing else, and the grid's own scrollbar had
 * no track to appear in. 280 covers the 246 and one row over.
 *
 * It is still deliberately not a comfort figure. Anything a floor reserves comes out of the other
 * pane or, at the extreme, out of the window - and a board taller than the window is the one thing
 * that puts a scrollbar on the page instead of in a grid. So it asks for the least that leaves a row
 * to scroll and leaves how the rest is divided to the splitter.
 */
const MIN_PANE_HEIGHT_PX = 280;

/*
 * Smallest board worth rendering: both panes at their floor with the splitter between them. Asked for
 * less, the board keeps this height and the page scrolls. That is the one scrollbar this screen will
 * accept outside a grid, and only for a window too short to fit two pagers - which has to give up
 * something, and a page scrollbar is at least reachable, whereas a clipped pager is not.
 */
const MIN_BOARD_HEIGHT_PX = MIN_PANE_HEIGHT_PX * 2 + SPLITTER_HEIGHT_PX;

/**
 * The split fraction, held to the pane floors for the board height it is being applied to.
 *
 * Applied on every render and not only while dragging: a fraction saved against a taller window, or a
 * window the user has since made shorter, would otherwise put a pane under the floor with no drag
 * involved.
 */
const clampTopFraction = (fraction, boardHeight) => {
    if (!boardHeight) {
        return fraction;
    }

    const min = MIN_PANE_HEIGHT_PX / boardHeight;
    const max = (boardHeight - SPLITTER_HEIGHT_PX - MIN_PANE_HEIGHT_PX) / boardHeight;

    // Unreachable while the board honours MIN_BOARD_HEIGHT_PX, and cheap insurance if it ever stops:
    // an even split leaves the two panes equally cramped rather than starving one of them.
    if (min > max) {
        return 0.5;
    }

    return Math.min(max, Math.max(min, fraction));
};

/** Breathing room under the board so the lower pager is not flush against the window edge. */
const BOARD_BOTTOM_GUTTER_PX = 8;

/**
 * FM Dispatch Management (legacy FMD0000) - the read-only dispatch board.
 *
 * Two panes divided by a draggable splitter, mirroring the Delphi form: the active top tab's grid
 * above, and a tab strip scoped to the selected row below. Reached from
 * /om/transactions/delivery-management/dispatch-management - the Delphi shows this program under both
 * FM and OM, but the web has no FM menu branch, so OM is the only way in.
 *
 * **There is no profile picker.** FMD0000 resolves its Dispatching Data Profile from the signed-in
 * user's IDDISPATCHER record (uFMD0000.pas:11807), which is why the desktop screen has no combo
 * either. The board asks the API for that profile once on load. When none resolves the grids render
 * uncoloured and unnarrowed - the Delphi's `DispatchingProfile <> ''` path - and nothing is said about
 * it, because from the user's point of view nothing is wrong.
 *
 * That profile also decides which of the three top tabs are shown at all - FMDISPATCHINGDP.TABDEFAULTS,
 * see visibleTopTabs above - and what the Drivers tab in the bottom pane is called.
 *
 * This slice covers three of the thirteen top tabs and fourteen of the twenty bottom tabs. None of the
 * 83 context-menu actions are implemented and nothing here writes to the database.
 */
const DispatchManagement = (props) => {
    const accessToken = props.oidc?.user?.access_token;

    // The whole OIDC user, not user.profile. FormField reads `currrentUser.access_token` (note its
    // spelling) to authorise its own layout request; handing it the profile sent
    // `Authorization: Bearer undefined`, the API answered 401, and the app bounced to
    // /NotAuthorized the moment the Totals tab was opened. Every other FormField caller in the repo
    // passes props.oidc.user.
    const currentUser = props.oidc?.user;

    // Whether the user may customise dynamic form layouts. It is a claim on the profile, not a prop
    // of this screen - passing props.dynamicFormSetting sent undefined and silently hid the
    // customise affordance.
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const [profile, setProfile] = useState(null);

    // Whether the profile lookup has settled, either way. Distinct from `profile !== null`: "no
    // profile configured" is a normal, permanent outcome, and the board runs uncoloured and
    // unnarrowed in it. The top grids wait on this rather than on the profile itself, so their first
    // load carries the profile code - see the note in BoardTabPane.
    const [profileResolved, setProfileResolved] = useState(false);

    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Per top tab, because the three tabs list different record sets: switching tabs must not point
    // the bottom pane at a row the new tab does not show.
    //
    // `focused` drives the row-scoped bottom tabs (Details, Contents); `selected` drives Totals, which
    // sums across a multi-row selection the way the Delphi's SelectedCountChange does.
    const [focusedRows, setFocusedRows] = useState({});
    const [selectedRows, setSelectedRows] = useState({});

    // Which top tabs this profile leaves standing. Recomputed with the profile, because TABDEFAULTS
    // can remove two of the three.
    const topTabs = useMemo(() => visibleTopTabs(profile), [profile]);

    // The landing effect below resets the index whenever the profile changes, but it runs after the
    // render that shrank the strip - so for one commit the index can point past the end of it. Held
    // in range here rather than there, because TabPanel reads it during that render.
    const selectedTabIndex = Math.min(activeTabIndex, topTabs.length - 1);

    const activeTab = topTabs[selectedTabIndex] ?? topTabs[0];

    // ---- profile -----------------------------------------------------------------------------
    useEffect(() => {
        if (!accessToken) {
            return undefined;
        }

        let cancelled = false;

        getEffectiveDispatchingProfile(accessToken)
            .then((resolved) => {
                if (!cancelled) {
                    setProfile(resolved);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    // A failed lookup is not the same as "no profile configured": the board still
                    // works, but the user should know why nothing is coloured.
                    notify(MESSAGES.DISPATCH_PROFILE_LOAD_FAILED, "error", 4000);
                }
            })
            .finally(() => {
                // In `finally`, not in `then`: a failed lookup has to release the grids too, or the
                // whole board sits on its placeholder because one request 500'd.
                if (!cancelled) {
                    setProfileResolved(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    // ---- landing tab -------------------------------------------------------------------------
    // The profile's DISPATCHINGTABDEFAULT decides where the board opens. Applied once per profile
    // change so it does not fight the user's own tab clicks afterwards.
    const appliedTabDefaultFor = useRef(null);

    useEffect(() => {
        const code = profile?.ProfileCode ?? null;
        if (appliedTabDefaultFor.current === code) {
            return;
        }
        appliedTabDefaultFor.current = code;

        // An unrecognised or absent value lands on Pick, matching the Delphi's ActivePageDefault.
        // Resolved against the VISIBLE tabs, not against all three: TABDEFAULTS can have removed the
        // one DISPATCHINGTABDEFAULT names, and index 0 of what is left is then the right answer -
        // the Delphi assigns mgtTop.ActivePage after SetDispatchingTabs has already hidden tabs.
        const landing = TAB_DEFAULT_TO_TOP_TAB[profile?.DispatchingTabDefault];
        const index = topTabs.findIndex((tab) => tab.id === landing);
        setActiveTabIndex(index >= 0 ? index : 0);
    }, [profile, topTabs]);

    const onFocusedRowChanged = useCallback(
        (row) => setFocusedRows((previous) => ({ ...previous, [activeTab.id]: row })),
        [activeTab.id]
    );

    const onSelectionChanged = useCallback(
        (rows) => setSelectedRows((previous) => ({ ...previous, [activeTab.id]: rows })),
        [activeTab.id]
    );

    // ---- board height ------------------------------------------------------------------------
    // Measured, not assumed. This was `height: calc(100vh - 150px)` in CSS, and 150 was a guess at
    // the shell's chrome: header, breadcrumb, facility picker and page padding. When the real chrome
    // is taller the board runs past the bottom of the window, and because the panes divide the board
    // rather than the viewport, what falls off the end is the lower pane's pager and scrollbar -
    // present in the DOM, unreachable on screen.
    //
    // Measuring the board's own top offset removes the guess and keeps working if the shell changes.
    const boardRef = useRef(null);
    const [boardHeight, setBoardHeight] = useState(null);

    // useLayoutEffect, not useEffect: this runs before paint, so the board is never briefly rendered
    // at the wrong height.
    useLayoutEffect(() => {
        const measure = () => {
            const board = boardRef.current;
            if (!board) {
                return;
            }

            const top = board.getBoundingClientRect().top;
            const available = window.innerHeight - top - BOARD_BOTTOM_GUTTER_PX;
            setBoardHeight(Math.max(MIN_BOARD_HEIGHT_PX, Math.round(available)));
        };

        measure();
        window.addEventListener("resize", measure);

        // The sidebar collapsing or a notification bar appearing moves the board without resizing the
        // window, and neither fires a resize event.
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
        if (observer && document.body) {
            observer.observe(document.body);
        }

        return () => {
            window.removeEventListener("resize", measure);
            if (observer) {
                observer.disconnect();
            }
        };
    }, []);

    // ---- splitter ----------------------------------------------------------------------------
    const [topFraction, setTopFraction] = useState(() => {
        try {
            const stored = Number(sessionStorage.getItem(SPLITTER_STORAGE_KEY));
            // Only "is this a fraction at all". How much of it is usable depends on the board height,
            // which this initialiser runs too early to know, so clampTopFraction decides that later.
            return stored > 0 && stored < 1 ? stored : DEFAULT_TOP_FRACTION;
        } catch {
            // Private windows and blocked site data throw on access; the default is fine.
            return DEFAULT_TOP_FRACTION;
        }
    });
    const [dragging, setDragging] = useState(false);

    // DevExtreme's own widgets set dx-state-hover from JS rather than in CSS, so the theme has no
    // :hover rule to inherit - see the note on the splitter markup below. Tracking it here is what
    // lets this handle take the theme's hover colour instead of a hardcoded one.
    const [hovered, setHovered] = useState(false);

    const onSplitterMouseDown = useCallback((e) => {
        e.preventDefault();
        setDragging(true);
    }, []);

    useEffect(() => {
        if (!dragging) {
            return undefined;
        }

        const onMove = (e) => {
            const board = boardRef.current;
            if (!board) {
                return;
            }

            const bounds = board.getBoundingClientRect();
            if (bounds.height <= 0) {
                return;
            }

            // bounds.height rather than the boardHeight state: it is the height being dragged
            // against, and it needs no dependency on this effect.
            const fraction = (e.clientY - bounds.top) / bounds.height;
            setTopFraction(clampTopFraction(fraction, bounds.height));
        };

        const onUp = () => setDragging(false);

        // On window, not on the splitter: the pointer routinely leaves the 8px bar mid-drag.
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);

        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, [dragging]);

    // Persisted on release rather than on every mousemove, which would write dozens of times a drag.
    useEffect(() => {
        if (dragging) {
            return;
        }
        try {
            sessionStorage.setItem(SPLITTER_STORAGE_KEY, String(topFraction));
        } catch {
            /* see the initialiser above */
        }
    }, [dragging, topFraction]);

    if (!accessToken) {
        return null;
    }

    const effectiveTopFraction = clampTopFraction(topFraction, boardHeight);

    return (
        // The standard page wrappers, for the same chrome and padding as every other screen. They do
        // not interfere with the height chain: .dm-board sets an absolute height rather than a
        // percentage, so it does not need its ancestors to be flex containers.
        <div className="row">
        <div className="col-md-12 main-page">
        <div
            // dx-splitter: not decoration, and not a claim to be a DevExtreme widget - the theme
            // scopes the resize handle's COLOURS to `.dx-splitter .dx-resize-handle` while its
            // layout rules are unscoped, so without this ancestor the handle below renders with
            // the right shape and cursor and no background at all. The -vertical modifier says the
            // panes are stacked, which is the flex-direction .dm-board already sets. Nothing else
            // in dx.material keys on these two: the rest of the widget's rules want
            // .dx-splitter-item, and the panes here are .dm-pane.
            className="dm-board dx-splitter dx-splitter-vertical"
            ref={boardRef}
            style={boardHeight ? { height: `${boardHeight}px` } : undefined}
        >
            {/* flexBasis, not height: the two panes and the splitter divide the board between them,
                so the bottom pane gets whatever the top one leaves rather than both being sized
                against the container and overflowing it. */}
            <div className="dm-pane dm-pane-top" style={{ flexBasis: `${effectiveTopFraction * 100}%` }}>
                <TabPanel
                    selectedIndex={selectedTabIndex}
                    onSelectedIndexChange={setActiveTabIndex}
                    height="100%"
                    deferRendering={true}
                >
                    {topTabs.map(({ id, title, Component }) => (
                        <TabItem key={id} title={title}>
                            <Component
                                accessToken={accessToken}
                                profile={profile}
                                profileResolved={profileResolved}
                                onFocusedRowChanged={onFocusedRowChanged}
                                onSelectionChanged={onSelectionChanged}
                            />
                        </TabItem>
                    ))}
                </TabPanel>
            </div>

            {/*
                Carries DevExtreme's own resize-handle classes so the theme paints it, rather than
                the hand-picked greys this used to have - the sidebar's splitter in AppContainer.js
                is a real <Splitter>, and its handle was a flat bar with grab dots where this was a
                thin 2px line, in a different grey again on hover and while dragging.

                Borrowing the classes rather than copying the colours is deliberate. dx.material's
                .dx-splitter .dx-resize-handle rules are the single source of the base, hover and
                active colours, so the two splitters cannot drift apart, and swapping the theme
                moves both. That selector is also why .dm-board above wears dx-splitter. What is
                left in dispatchManagement.css is the 8px thickness, which DevExtreme sets inline on
                its own handle and so is not in the stylesheet to inherit.

                dx-state-hover and dx-state-active are the state classes the theme keys on;
                DevExtreme's widgets set them from JS, which is why they are React state here and
                not :hover / :active in CSS. The -vertical modifier means a vertically split pair -
                a horizontal bar with a row-resize cursor - and the icon it pairs with is
                dx-icon-handlehorizontal; the sidebar, split the other way, gets the opposite pair.
            */}
            <div
                className={
                    "dm-splitter dx-resize-handle dx-resize-handle-vertical dx-resize-handle-resizable" +
                    (hovered ? " dx-state-hover" : "") +
                    (dragging ? " dx-state-active" : "")
                }
                onMouseDown={onSplitterMouseDown}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                role="separator"
                aria-orientation="horizontal"
                aria-label="Resize the dispatch board panes"
            >
                <div className="dx-resize-handle-icon dx-icon dx-icon-handlehorizontal" />
            </div>

            <div className="dm-pane dm-pane-bottom">
                <BottomTabs
                    accessToken={accessToken}
                    topTab={activeTab.id}
                    profile={profile}
                    selectedRow={focusedRows[activeTab.id] ?? null}
                    selectedRows={selectedRows[activeTab.id] ?? []}
                    currentUser={currentUser}
                    dynamicFormSetting={dynamicFormSetting}
                />
            </div>
        </div>
        </div>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(DispatchManagement);
